import { Component, inject, Injector, OnInit } from '@angular/core';
import { Database, get, ref, remove, set, update } from "@angular/fire/database";
import { AuthService } from 'src/app/services/auth.service';
import { BaseComponent } from 'src/app/shared/baseComponent';

@Component({
  selector: 'app-performance',
  templateUrl: './performance.component.html',
  styleUrls: ['./performance.component.css']
})
export class PerformanceComponent extends BaseComponent implements OnInit {
  database = inject(Database);
  isAuthenticatedWithFirebase = false
  showinfo = false
  exercises: any = []
  exercise: any = {}
  players: any = []
  player: any = {}
  showPlayerModal = false
  cutoffDate: string = '' // bound to date input for bulk delete
  showRawData: boolean = false
  flagThreshold: number = 8
  tableSortCol: string = 'lastRegistered'
  tableSortDir: 'asc' | 'desc' = 'desc'
  checkedExercises: Set<string> = new Set()

  get sortedExercises(): any[] {
    return [...this.exercises].sort((a: any, b: any) => {
      let valA = a[this.tableSortCol]
      let valB = b[this.tableSortCol]
      if (this.tableSortCol === 'property') {
        valA = (valA || '').toLowerCase()
        valB = (valB || '').toLowerCase()
      } else if (this.tableSortCol === 'scenarioid') {
        valA = (valA || '').toLowerCase()
        valB = (valB || '').toLowerCase()
      } else if (this.tableSortCol === 'nPlayers') {
        valA = valA ?? -1
        valB = valB ?? -1
      } else if (this.tableSortCol === 'lastRegistered') {
        valA = valA ? new Date(valA).getTime() : -Infinity
        valB = valB ? new Date(valB).getTime() : -Infinity
      }
      if (valA < valB) return this.tableSortDir === 'asc' ? -1 : 1
      if (valA > valB) return this.tableSortDir === 'asc' ? 1 : -1
      return 0
    })
  }

  sortBy(column: string) {
    if (this.tableSortCol === column) {
      this.tableSortDir = this.tableSortDir === 'asc' ? 'desc' : 'asc'
    } else {
      this.tableSortCol = column
      this.tableSortDir = 'asc'
    }
  }

  get allChecked(): boolean {
    return this.exercises.length > 0 && this.exercises.every((e: any) => this.checkedExercises.has(e.property))
  }

  toggleCheck(id: string) {
    if (this.checkedExercises.has(id)) { this.checkedExercises.delete(id) } else { this.checkedExercises.add(id) }
  }

  toggleAll() {
    if (this.allChecked) { this.checkedExercises.clear() } else { this.exercises.forEach((e: any) => this.checkedExercises.add(e.property)) }
  }

  async deleteChecked() {
    if (this.checkedExercises.size === 0) { alert('No records selected.'); return }
    if (!confirm(`Delete ${this.checkedExercises.size} selected exercise(s)?`)) { return }
    this.loader.show()
    for (const id of Array.from(this.checkedExercises)) {
      const refe = ref(this.database, 'performance/' + id)
      await remove(refe)
    }
    this.checkedExercises.clear()
    await this.getData()
    this.loader.hide()
  }


  constructor(injector: Injector, private authService: AuthService) {
    super(injector)
  }

  async ngOnInit(): Promise<void> {
    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfullLogin())
  }

  openAnalysis(exercise: any) {
    this.router.navigate(['exerciseanalysis'], { queryParams: { exerciseId: exercise.property } })
  }

  async signInWithGoogle() {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle()
    if (this.isAuthenticatedWithFirebase) { await this.onSuccessfullLogin() }
  }

  private async onSuccessfullLogin() {
    await this.getData()
  }

  async getData() {
    this.loader.show()
    this.exercises = []
    const refe = ref(this.database, 'performance');
    const snapshot = await get(refe);
    if (snapshot.exists()) {
      const temp = snapshot.val();
      const tempArray = this.collectionToArrayFB(temp)
      tempArray.forEach((exer: any) => {
        let nPlayers = 0
        let lastRegistered: Date | null = null
        Object.keys(exer).forEach((key: any) => {
          nPlayers++
          if (key === 'property' || key === 'key') { return }
          const registeredAtStr = exer[key]?.["registeredAt"]
          if (registeredAtStr) {
            const registeredAt = new Date(registeredAtStr)
            if (!isNaN(registeredAt.getTime())) {
              if (lastRegistered === null || registeredAt > lastRegistered) {
                lastRegistered = registeredAt
              }
            }
          }
        })
        const rec: any = { property: exer.property, lastRegistered: lastRegistered, nPlayers: Math.max(0, nPlayers - 2), scenarioid: null }
        this.exercises.push(rec)
      })
      // Sort exercises by lastRegistered descending (nulls last)
      this.exercises.sort((a: any, b: any) => {
        if (!a.lastRegistered && !b.lastRegistered) return 0
        if (!a.lastRegistered) return 1
        if (!b.lastRegistered) return -1
        return new Date(b.lastRegistered).getTime() - new Date(a.lastRegistered).getTime()
      })
    }

    // Fetch scenarioid for each exercise from exercises/<exerciseId>
    for (const exer of this.exercises) {
      try {
        const exerciseRef = ref(this.database, 'exercises/' + exer.property)
        const exerSnap = await get(exerciseRef)
        if (exerSnap.exists()) {
          const val = exerSnap.val()
            exer.scenarioid = val.scenarioid || val.scenarioId || null
        }
      } catch (err) {
        console.warn('Could not load exercise node for', exer.property, err)
      }
    }

    this.loader.hide()
  }
  async selectExercise(exercise: any) {
    this.exercise = exercise
    this.players = []
    const refe = ref(this.database, 'performance/' + exercise.property);
    const snapshot = await get(refe);
    if (snapshot.exists()) {
      const temp = snapshot.val();
      const tempArray = this.collectionToArrayFB(temp)
      tempArray.forEach((player: any) => {
        let rec = {
          property: player.property,
          registeredAt: player.registeredAt,
          userAgent: player.userAgent,
          loadDuration: player.loadComponents?.duration_ms,
          renderDuration: player.render?.durationFor100CallsMs
        }
        this.players.push(rec)
      })
    }
    this.players.sort((a: any, b: any) => {
      return new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime();
    })
  }


  async showPlayer(player: any) {
      this.router.navigate(['performancedetail'],{ queryParams: { exerciseid:this.exercise.property,playername: player.property } } )

    }
  async deleteExercise(exercise: any) {
      this.loader.show()
      const refe = ref(this.database, 'performance/' + exercise.property);
      await remove(refe);
      await this.getData()
      this.loader.hide()
    }
  
  flagLessThan() {
    this.exercises.forEach((e: any) => {
      if ((e.nPlayers ?? 0) < this.flagThreshold) {
        this.checkedExercises.add(e.property)
      }
    })
  }

  async deleteBeforeDate() {
    if (!this.cutoffDate) { alert('Please select a date first.'); return }
    const cutoff = new Date(this.cutoffDate)
    if (isNaN(cutoff.getTime())) { alert('Invalid date'); return }
    // Use midnight of selected date
    cutoff.setHours(0,0,0,0)
    this.loader.show()
    const perfRef = ref(this.database, 'performance');
    const snapshot = await get(perfRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      const updates: any = {}
      Object.keys(data).forEach(exId => {
        const exercise = data[exId]
        Object.keys(exercise).forEach(playerId => {
          if (playerId === 'property' || playerId === 'key') return
            const rec = exercise[playerId]
            const regStr = rec?.registeredAt
            if (regStr) {
              const regDate = new Date(regStr)
              if (!isNaN(regDate.getTime()) && regDate < cutoff) {
                updates[`performance/${exId}/${playerId}`] = null
              }
            }
        })
      })
      if (Object.keys(updates).length === 0) {
        this.loader.hide();
        alert('No records found before selected date.')
        return
      }
      const rootRef = ref(this.database)
      await update(rootRef, updates)
      await this.getData()
    }
    this.loader.hide()
  }
  }
